import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import PageHero from "@/components/site/PageHero";
import HapoIntelligenceStorefront from "@/components/solutions/HapoIntelligenceStorefront";
import { Eyebrow, Reveal, GoldButton, OutlineButton, EASE } from "@/components/site/motion";
import { IMAGES, solutions, companyBySlug } from "@/data/content";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

const inputDark =
  "w-full border-b border-white/25 bg-transparent py-3 text-sm text-white placeholder:text-smoke focus:border-gold focus:outline-none transition-colors duration-300";

export default function Solutions() {
  useSEO(
    "Solutions — Hapo Group",
    "From the screen on the wall to the intelligence behind it — connected solutions across technology, cloud & AI, media, music, energy and manufacturing from the Hapo Group."
  );
  const [form, setForm] = useState({ name: "", email: "", solution: "General / Not sure yet", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.sendEnquiry({
        name: form.name,
        email: form.email,
        company: "Solutions Enquiry",
        message: `Solution: ${form.solution}\n\n${form.message}`,
      });
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch.");
    } catch {
      toast.error("Could not send your enquiry — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Group Solutions"
        lines={["One group.", "Every solution."]}
        description="From the screen on the wall to the intelligence behind it — the Hapo Group delivers connected solutions across technology, media, energy and data."
        image={IMAGES.network}
      />

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Our approach to solutions">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>What We Bring Forward</Eyebrow>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p className="text-xl font-medium leading-relaxed tracking-tight text-ink md:text-2xl lg:text-3xl">
              Our companies don't operate in isolation. Together they form a single, connected offering — hardware we
              manufacture ourselves, power we generate, audiences we measure, advertising we sell and intelligence we
              build. Whatever your challenge, there is a Hapo solution behind it.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="One connected value chain">
        <Eyebrow>One Connected Value Chain</Eyebrow>
        <Reveal>
          <p className="mt-8 max-w-5xl text-3xl font-extrabold leading-tight tracking-tighter text-white sm:text-4xl lg:text-5xl">
            Hapo Electronics manufactures the screen. Hapo Energy powers it. Hapo Cloud's Vision AI reads the
            audience. Hapo Media sells the advertising. Hapo Intelligence measures it all —{" "}
            <span className="text-gold">and Hapo Sound scores the room.</span>
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12">
            <OutlineButton to="/companies" testId="solutions-explore-companies">
              Explore Our Companies
            </OutlineButton>
          </div>
        </Reveal>
      </section>

      <HapoIntelligenceStorefront />

      <section className="bg-mist px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Group solutions list" data-testid="solutions-list">
        <Eyebrow>The Solutions</Eyebrow>
        <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
          Nine companies. One connected offering.
        </h2>
        <div className="mt-20">
          {solutions.map((s, i) => (
            <div
              key={s.num}
              data-testid={`solution-item-${s.num}`}
              className={`grid items-center gap-10 py-16 md:grid-cols-2 lg:gap-20 lg:py-20 ${i > 0 ? "border-t border-ink/10" : ""}`}
            >
              <Reveal className={i % 2 === 1 ? "md:order-2" : ""}>
                <p className="text-5xl font-extrabold tracking-tighter text-gold lg:text-6xl">{s.num}</p>
                <h3 className="mt-6 text-3xl font-extrabold tracking-tighter text-ink sm:text-4xl">{s.title}</h3>
                <p className="mt-6 max-w-lg text-sm leading-loose text-smoke md:text-base">{s.text}</p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-smoke">Delivered by</span>
                  {s.deliveredBy.map((slug) => {
                    const c = companyBySlug(slug);
                    return (
                      <Link
                        key={slug}
                        to={`/companies/${slug}`}
                        data-testid={`solution-${s.num}-${slug}`}
                        className="rounded-full border border-ink/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:border-gold hover:text-gold"
                      >
                        {c?.name}
                      </Link>
                    );
                  })}
                  {s.comingSoon && (
                    <span className="rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-gold">
                      Coming Soon
                    </span>
                  )}
                </div>
              </Reveal>
              <Reveal delay={0.1} className={i % 2 === 1 ? "md:order-1" : ""}>
                <div className="overflow-hidden">
                  <motion.img
                    initial={{ scale: 1.12 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, ease: EASE }}
                    src={s.image}
                    alt={`${s.title} — placeholder image`}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ink px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Solutions enquiry" data-testid="solutions-enquiry-section">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Talk to Us</Eyebrow>
            <h2 className="mt-5 text-4xl font-extrabold leading-tight tracking-tighter text-white sm:text-5xl">
              One conversation connects you to the whole group.
            </h2>
            <p className="mt-8 max-w-lg text-sm leading-loose text-smoke md:text-base">
              Tell us what you're trying to solve — pick the solution you're interested in and we'll bring the right
              companies to the table.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <div data-testid="solutions-success-message" className="border-l-2 border-gold bg-white/5 p-10">
                <h3 className="text-2xl font-bold tracking-tight text-white">
                  Thank you, {form.name.split(" ")[0]}.
                </h3>
                <p className="mt-4 text-sm leading-loose text-smoke">
                  Your enquiry about <span className="font-semibold text-white">{form.solution}</span> has been
                  received. The right team will get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="solutions-form">
                <div className="grid gap-8 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Name *</span>
                    <input
                      required
                      value={form.name}
                      onChange={update("name")}
                      data-testid="solutions-name-input"
                      className={inputDark}
                      placeholder="Your full name"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Email *</span>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      data-testid="solutions-email-input"
                      className={inputDark}
                      placeholder="you@company.co.za"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">
                    Which solution are you interested in?
                  </span>
                  <select
                    value={form.solution}
                    onChange={update("solution")}
                    data-testid="solutions-solution-select"
                    className={`${inputDark} cursor-pointer`}
                  >
                    {["General / Not sure yet", ...solutions.map((s) => s.title)].map((o) => (
                      <option key={o} value={o} className="text-ink">
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Message *</span>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="solutions-message-input"
                    className={`${inputDark} resize-none`}
                    placeholder="Tell us about your project or requirement"
                  />
                </label>
                <GoldButton type="submit" testId="solutions-submit-button">
                  {sending ? "Sending..." : "Send Enquiry"}
                </GoldButton>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
